import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="toast"
export default class extends Controller {
  static targets = ["container"];
  static values = {
    position: { type: String, default: "top-center" },
    layout: { type: String, default: "default" }, // "default" (stacked) or "expanded" (all visible)
    gap: { type: Number, default: 14 }, // Gap between toasts in expanded mode
    autoDismissDuration: { type: Number, default: 4000 },
    limit: { type: Number, default: 3 }, // Maximum number of visible toasts
  };

  connect() {
    this.toasts = [];
    this.heights = []; // Track toast heights like Sonner
    
    // Restore timers from global storage if they exist
    if (window.toastTimers) {
      this.autoDismissTimers = window.toastTimers;
    } else {
      this.autoDismissTimers = {}; // Initialize timers object
    }
    
    this.expanded = this.layoutValue === "expanded";
    this.interacting = false;

    // Store current position in a global variable that persists across interactions
    if (!window.currentToastPosition) {
      window.currentToastPosition = this.positionValue;
    } else {
      // Restore the position from the global variable
      this.positionValue = window.currentToastPosition;
    }

    // Set initial position classes
    this.updatePositionClasses();

    // Make toast function globally available
    if (!window.toast) {
      window.toast = this.showToast.bind(this);
    }

    // Bind event handlers so they can be properly removed
    this.boundHandleToastShow = this.handleToastShow.bind(this);
    this.boundHandleLayoutChange = this.handleLayoutChange.bind(this);
    this.boundBeforeCache = this.beforeCache.bind(this);

    // Listen for toast events
    window.addEventListener("toast-show", this.boundHandleToastShow);
    window.addEventListener("set-toasts-layout", this.boundHandleLayoutChange);
    document.addEventListener("turbo:before-cache", this.boundBeforeCache);
  }

  updatePositionClasses() {
    const container = this.containerTarget;
    // Remove all position classes
    container.classList.remove(
      "right-0",
      "left-0",
      "left-1/2",
      "-translate-x-1/2",
      "top-0",
      "bottom-0",
      "mt-4",
      "mb-4",
      "mr-4",
      "ml-4",
      "sm:mt-6",
      "sm:mb-6",
      "sm:mr-6",
      "sm:ml-6"
    );

    // Add new position classes
    const classes = this.positionClasses.split(" ");
    container.classList.add(...classes);
  }

  disconnect() {
    // Store timers globally so they survive disconnect/reconnect cycles
    if (!window.toastTimers) {
      window.toastTimers = {};
    }
    Object.assign(window.toastTimers, this.autoDismissTimers || {});
    
    // Remove event listeners using the bound references
    window.removeEventListener("toast-show", this.boundHandleToastShow);
    window.removeEventListener("set-toasts-layout", this.boundHandleLayoutChange);
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache);

    // DON'T clear timers - they need to continue running
    // DON'T clean up toasts on disconnect - they persist across Turbo navigation
  }

  showToast(message, options = {}) {
    const detail = {
      type: options.type || "default",
      message: message,
      description: options.description || "",
      position: options.position || window.currentToastPosition || this.positionValue, // Use stored position
      html: options.html || "",
      action: options.action || null,
      secondaryAction: options.secondaryAction || null,
    };

    window.dispatchEvent(new CustomEvent("toast-show", { detail }));
  }

  handleToastShow(event) {
    event.stopPropagation();

    // Update container position if a position is specified for this toast
    if (event.detail.position) {
      this.positionValue = event.detail.position;
      window.currentToastPosition = event.detail.position; // Store globally
      this.updatePositionClasses();
    }

    const toast = {
      id: `toast-${Math.random().toString(16).slice(2)}`,
      mounted: false,
      removed: false,
      message: event.detail.message,
      description: event.detail.description,
      type: event.detail.type,
      html: event.detail.html,
      action: event.detail.action,
      secondaryAction: event.detail.secondaryAction,
    };

    // Add toast at the beginning of the array (newest first)
    this.toasts.unshift(toast);

    // Enforce toast limit synchronously to prevent race conditions
    const activeToasts = this.toasts.filter((t) => !t.removed);
    if (activeToasts.length > this.limitValue) {
      const oldestActiveToast = activeToasts[activeToasts.length - 1];
      if (oldestActiveToast && !oldestActiveToast.removed) {
        this.removeToast(oldestActiveToast.id, true);
      }
    }

    this.renderToast(toast);
  }

  handleLayoutChange(event) {
    this.layoutValue = event.detail.layout;
    this.expanded = this.layoutValue === "expanded";
    this.updateAllToasts();
  }

  beforeCache() {
    // DON'T clear toasts on cache - let them persist across Turbo navigation
    // this.clearAllToasts();
    // Reset position to default on navigation
    window.currentToastPosition = this.element.dataset.toastPositionValue || "top-center";
  }

  clearAllToasts() {
    // Remove all toast elements from DOM
    const container = this.containerTarget;
    if (container) {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    }

    // Clear arrays
    this.toasts = [];
    this.heights = [];

    // Clear all timers
    if (this.autoDismissTimers) {
      Object.values(this.autoDismissTimers).forEach((timer) => clearTimeout(timer));
      this.autoDismissTimers = {};
    }
  }

  handleMouseEnter() {
    if (this.layoutValue === "default") {
      this.expanded = true;
      this.updateAllToasts();
    }
  }

  handleMouseLeave() {
    if (this.layoutValue === "default" && !this.interacting) {
      this.expanded = false;
      this.updateAllToasts();
    }
  }

  renderToast(toast) {
    const container = this.containerTarget;
    const li = this.createToastElement(toast);
    const ol = container.querySelector('ol');
    if (ol) {
      ol.insertBefore(li, ol.firstChild);
    } else {
      container.insertBefore(li, container.firstChild);
    }

    // Measure height after a short delay to ensure rendering is complete
    requestAnimationFrame(() => {
      const toastEl = document.getElementById(toast.id);
      if (toastEl) {
        const height = toastEl.getBoundingClientRect().height;

        // Add height to the beginning of heights array
        this.heights.unshift({
          toastId: toast.id,
          height: height,
        });

        // Count only active (non-removed) toasts
        const activeToasts = this.toasts.filter((t) => !t.removed);

        // Trigger mount animation
        requestAnimationFrame(() => {
          toast.mounted = true;
          toastEl.dataset.mounted = "true";

          // Update all toast positions
          this.updateAllToasts();
        });

        // Schedule auto-dismiss for visible toasts
        const activeToastIndex = activeToasts.findIndex((t) => t.id === toast.id);
        if (activeToastIndex < this.limitValue) {
          this.scheduleAutoDismiss(toast.id);
        }
      }
    });
  }

  createToastElement(toast) {
    const li = document.createElement("li");
    li.id = toast.id;
    li.className = "toast-item";
    li.tabIndex = 0;
    li.setAttribute("data-mounted", "false");
    li.setAttribute("data-removed", "false");
    li.setAttribute("data-toast-type", toast.type);
    li.style.setProperty("--initial-height", "auto");

    // Use custom HTML if provided, otherwise use default template
    if (toast.html) {
      li.innerHTML = toast.html;
    } else {
      li.innerHTML = this.toastTemplate(toast);
    }

    // Action button handlers are added via data-action attributes in the template
    if (toast.action) {
      const actionBtn = li.querySelector('[data-action-button]');
      if (actionBtn) {
        actionBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (typeof toast.action.onClick === 'function') {
            toast.action.onClick();
          }
          this.removeToast(toast.id);
        });
      }
    }

    if (toast.secondaryAction) {
      const secondaryBtn = li.querySelector('[data-secondary-action-button]');
      if (secondaryBtn) {
        secondaryBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (typeof toast.secondaryAction.onClick === 'function') {
            toast.secondaryAction.onClick();
          }
          this.removeToast(toast.id);
        });
      }
    }

    return li;
  }

  toastTemplate(toast) {
    const styles = this.getStylesForType(toast.type);
    const iconSvg = this.getIconForType(toast.type);
    const actionButtons = this.getActionButtons(toast);

    return `
      <div class="flex w-full items-start gap-3 rounded-lg border ${styles.border} ${styles.bg} p-4 shadow-md">
        ${iconSvg}
        <div class="flex flex-1 flex-col gap-0.5 text-left min-w-0">
          <div class="flex items-center justify-between gap-2">
            <p class="text-sm font-semibold text-left ${styles.title}">${this.escapeHtml(toast.message)}</p>
            <button type="button" data-toast-id="${toast.id}" class="inline-flex size-5 shrink-0 items-center justify-center rounded-md ${styles.close} transition-colors hover:opacity-80">
              <svg xmlns="http://www.w3.org/2000/svg" class="size-4" width="18" height="18" viewBox="0 0 18 18"><g fill="currentColor"><path d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z"></path></g></svg>
            </button>
          </div>
          ${toast.description ? `<p class="text-sm text-left ${styles.text}">${this.escapeHtml(toast.description)}</p>` : ''}
          ${actionButtons}
        </div>
      </div>
    `;
  }

  getStylesForType(type) {
    const styles = {
      success: { bg: "bg-green-50",  border: "border-green-200",  title: "text-green-800",  text: "text-green-700",  close: "text-green-500",  icon_bg: "bg-green-100", icon_text: "text-green-600" },
      error:   { bg: "bg-red-50",    border: "border-red-200",    title: "text-red-800",    text: "text-red-700",    close: "text-red-500",    icon_bg: "bg-red-100",   icon_text: "text-red-600" },
      danger:  { bg: "bg-red-50",    border: "border-red-200",    title: "text-red-800",    text: "text-red-700",    close: "text-red-500",    icon_bg: "bg-red-100",   icon_text: "text-red-600" },
      warning: { bg: "bg-amber-50",  border: "border-amber-200",  title: "text-amber-800",  text: "text-amber-700",  close: "text-amber-500",  icon_bg: "bg-amber-100", icon_text: "text-amber-600" },
      info:    { bg: "bg-blue-50",   border: "border-blue-200",   title: "text-blue-800",   text: "text-blue-700",   close: "text-blue-500",   icon_bg: "bg-blue-100",  icon_text: "text-blue-600" },
      default: { bg: "bg-white",     border: "border-gray-200",   title: "text-gray-900",   text: "text-gray-600",   close: "text-gray-400",   icon_bg: "bg-gray-100",  icon_text: "text-gray-600" },
      loading: { bg: "bg-white",     border: "border-gray-200",   title: "text-gray-900",   text: "text-gray-600",   close: "text-gray-400",   icon_bg: "bg-gray-100",  icon_text: "text-gray-600" },
    };
    return styles[type] || styles.default;
  }

  getIconForType(type) {
    const styles = this.getStylesForType(type);
    const icons = {
      success: `<div class="flex size-6 shrink-0 items-center justify-center rounded-full ${styles.icon_bg}"><svg xmlns="http://www.w3.org/2000/svg" class="size-4 ${styles.icon_text}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></div>`,
      error:   `<div class="flex size-6 shrink-0 items-center justify-center rounded-full ${styles.icon_bg}"><svg xmlns="http://www.w3.org/2000/svg" class="size-4 ${styles.icon_text}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg></div>`,
      danger:  `<div class="flex size-6 shrink-0 items-center justify-center rounded-full ${styles.icon_bg}"><svg xmlns="http://www.w3.org/2000/svg" class="size-4 ${styles.icon_text}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg></div>`,
      warning: `<div class="flex size-6 shrink-0 items-center justify-center rounded-full ${styles.icon_bg}"><svg xmlns="http://www.w3.org/2000/svg" class="size-4 ${styles.icon_text}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/></svg></div>`,
      info:    `<div class="flex size-6 shrink-0 items-center justify-center rounded-full ${styles.icon_bg}"><svg xmlns="http://www.w3.org/2000/svg" class="size-4 ${styles.icon_text}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/></svg></div>`,
      loading: `<div class="flex size-6 shrink-0 items-center justify-center"><svg class="size-4 animate-spin text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg></div>`,
      default: `<div class="flex size-6 shrink-0 items-center justify-center rounded-full ${styles.icon_bg}"><svg xmlns="http://www.w3.org/2000/svg" class="size-4 ${styles.icon_text}" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/></svg></div>`,
    };
    return icons[type] || icons.default;
  }

  getActionButtons(toast) {
    if (!toast.action && !toast.secondaryAction) return '';
    const styles = this.getStylesForType(toast.type);

    const buttons = [];

    if (toast.action) {
      buttons.push(`
        <button type="button" data-action-button class="inline-flex items-center gap-1.5 text-sm font-medium ${styles.title} transition-colors hover:opacity-80">
          ${this.escapeHtml(toast.action.label)}
        </button>
      `);
    }

    if (toast.secondaryAction) {
      buttons.push(`
        <button type="button" data-secondary-action-button class="inline-flex items-center gap-1.5 text-sm font-medium ${styles.text} transition-colors hover:opacity-80">
          ${this.escapeHtml(toast.secondaryAction.label)}
        </button>
      `);
    }

    return `<div class="mt-2 flex items-center gap-3">${buttons.join('')}</div>`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  updateAllToasts() {
    const activeToasts = this.toasts.filter((t) => !t.removed);
    
    activeToasts.forEach((toast, index) => {
      const toastEl = document.getElementById(toast.id);
      if (!toastEl) return;

      const offset = this.calculateOffset(index);
      const scale = this.calculateScale(index);
      const opacity = index < this.limitValue ? 1 : 0;

      toastEl.style.setProperty("--toast-offset", `${offset}px`);
      toastEl.style.setProperty("--toast-scale", scale);
      toastEl.style.setProperty("--toast-opacity", opacity);
    });
  }

  calculateOffset(index) {
    if (this.expanded) {
      // In expanded mode, stack toasts with gaps
      let offset = 0;
      for (let i = 0; i < index; i++) {
        const heightData = this.heights.find(h => h.toastId === this.toasts.filter(t => !t.removed)[i].id);
        if (heightData) {
          offset += heightData.height + this.gapValue;
        }
      }
      return offset;
    } else {
      // In stacked mode, show small peek of toasts behind
      return index * 8;
    }
  }

  calculateScale(index) {
    if (this.expanded) {
      return 1;
    } else {
      // Gradually scale down toasts that are behind
      return 1 - (index * 0.05);
    }
  }

  removeToast(toastId, skipAnimation = false) {
    const toastEl = document.getElementById(toastId);
    if (!toastEl) {
      return;
    }

    // Find toast in array (may not exist if controller was reconnected)
    const toast = this.toasts.find((t) => t.id === toastId);
    if (toast && !toast.removed) {
      toast.removed = true;
    }

    // Mark as removed
    toastEl.dataset.removed = "true";

    // Clear any auto-dismiss timer for this toast
    if (this.autoDismissTimers && this.autoDismissTimers[toastId]) {
      clearTimeout(this.autoDismissTimers[toastId]);
      delete this.autoDismissTimers[toastId];
      // Also clean up global storage
      if (window.toastTimers && window.toastTimers[toastId]) {
        delete window.toastTimers[toastId];
      }
    }

    if (skipAnimation) {
      // Immediate removal
      toastEl.remove();
      this.heights = this.heights.filter(h => h.toastId !== toastId);
      this.updateAllToasts();
    } else {
      // Animate out
      setTimeout(() => {
        toastEl.remove();
        this.heights = this.heights.filter(h => h.toastId !== toastId);
        this.updateAllToasts();
      }, 200);
    }
  }

  scheduleAutoDismiss(toastId) {
    const timerId = setTimeout(() => {
      this.removeToast(toastId);
    }, this.autoDismissDurationValue);
    
    // Store timer in both local and global storage
    this.autoDismissTimers[toastId] = timerId;
    if (!window.toastTimers) {
      window.toastTimers = {};
    }
    window.toastTimers[toastId] = timerId;
  }

  handleClick(event) {
    // Handle clicks on dismiss buttons using event delegation
    const dismissBtn = event.target.closest('[data-toast-id]');
    if (dismissBtn && dismissBtn.hasAttribute('data-toast-id')) {
      event.preventDefault();
      event.stopPropagation();
      const toastId = dismissBtn.dataset.toastId;
      this.removeToast(toastId);
    }
  }

  get positionClasses() {
    const positions = {
      "top-left": "top-0 left-0 mt-4 ml-4 sm:mt-6 sm:ml-6",
      "top-center": "top-0 left-1/2 -translate-x-1/2 mt-4 sm:mt-6",
      "top-right": "top-0 right-0 mt-4 mr-4 sm:mt-6 sm:mr-6",
      "bottom-left": "bottom-0 left-0 mb-4 ml-4 sm:mb-6 sm:ml-6",
      "bottom-center": "bottom-0 left-1/2 -translate-x-1/2 mb-4 sm:mb-6",
      "bottom-right": "bottom-0 right-0 mb-4 mr-4 sm:mb-6 sm:mr-6",
    };
    return positions[this.positionValue] || positions["top-center"];
  }
}

